import { Box } from "@mui/material";
import dayjs from "dayjs";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  formClassCorp,
  formClassFacility,
  FormKbn,
  Status,
} from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { labelids } from "../../assets/data/TkExplanationProperty";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import TkStrpper from "../../components/atoms/TkStepper";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkContractorAndNumberLine from "../../components/molecules/TkContractorAndNumberLine";
import TkDateDialog from "../../components/molecules/TkDateDialog";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { BusinessContext } from "../../context/businessContext";
import { GlobalContext } from "../../context/globalContext";
import { IAPI_PRE004_Request, IAPI_PRE005_Response } from "../../type/apiType";
import "./_style.scss";
import { httpAPI_PRE004, httpAPI_PRE005 } from "./api/request";
import { fakeTableList, schema, stepperData } from "./parts";
import _ from "lodash";

/**
 * SCRC080009コンポーネント。
 * このコンポーネントは、TkInfoManualとTkFakeTableをレンダリングします。
 * @returns JSX.Element
 */
const SCRC080009 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState } = useContext(BusinessContext);
  const [statecorpInfoList, setStatecorpInfoList] = useState<any>([]);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const [openUploadFile, setOpenUploadFile] = useState(false);
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);
  const [activeStepData, setActiveStepData] = useState(1);
  const [requestData, setRequestData] = useState<IAPI_PRE004_Request>({});
  const [showButton, setShowButton] = useState(false);
  const [resData, setResData] = useState<IAPI_PRE005_Response>({});

  const isKiko =
    state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO;
  const buttonName = isKiko ? "申請" : "機構へ提出";
  const contractorNo = isKiko
    ? businessState.common?.contractorNo
    : state.authInfo?.contractorNo;
  const applyId = location.state?.applyId;

  const handleFileUpload = (formFiles: any[]) => {
    let filesTmp: IAPI_PRE004_Request["formList"] = [];
    formFiles &&
      formFiles.length > 0 &&
      formFiles.forEach((item: any, index: number) => {
        const tmp = {
          serialNo: index + 1,
          facilityNo: "",
          staffNo: "",
          formKbn: FormKbn["法人書類"],
          formClass: formClassFacility["部分解除同意書"],
          fileName: item.formName,
          fileBase64: item.fileBase64,
        };
        filesTmp?.push(tmp);
      });
    return filesTmp;
  };

  const handleInit = async () => {
    try {
      const res = await httpAPI_PRE005(
        applyId
          ? { applyId }
          : {
              contractorNo,
              year: +(state.authInfo?.fiscalYear ?? 0),
            }
      );

      if (res.resultCd === "I0000") {
        setResData(res);

        const activeContents =
          res?.latestApplyStatus === null ||
          res?.latestApplyStatus === Status["届出待ち"] ||
          res?.latestApplyStatus === Status["差戻し一時保存"];
        setShowButton(activeContents);

        let resArr = stateFakeTableList.current;
        // 初期表示
        //延長の対象となる掛金（年度）
        resArr.find((item) => item.keyword === "premiumsForExtensio")!.text =
          res.year?.toString().slice(0, 4) + "　年度分";
        //延長の対象となる掛金（金額）
        resArr.find(
          (item) => item.keyword === "premiumsForExtensioAmount"
        )!.text = res.premiumAmt?.toString() + "　円";
        //希望する延長期限日付
        const applyDate = resArr.find((item) => item.keyword === "applyDate")!;
        applyDate.setDefaultValue = res.expectDeadline;
        // 希望する延長期限は最大当年度の１月３１日まで、または2099年です
        applyDate.disabled = !activeContents;
        if (res.year) {
          const maxDate = new Date(`${res.year + 1}-01-31`);
          applyDate.maxDate =
            maxDate < new Date("2099-12-31") ? maxDate : new Date("2099-12-31");
        }
        //延長前の納付期限
        resArr.find(
          (item) => item.keyword === "deadlineBeforeExtension"
        )!.text = res.paymentDeadline;
        //延長申請の理由証明書
        const fileList = res.formList?.find(
          (item: any) =>
            item.formKbn === FormKbn["法人書類"] &&
            item.formClass === formClassCorp["掛金納付延長申請理由書"]
        );
        if (fileList) {
          let fileUpload = resArr.find(
            (item) => item.keyword === "fileUpload"
          )!;
          if (activeContents) {
            fileUpload.setDefaultValue = [
              {
                formName: fileList?.fileName,
                fileBase64: fileList?.fileBase64,
              },
            ];
          } else {
            fileUpload.type = "downloadLink";
            fileUpload.downloadBase64 = true;
            fileUpload.dataname = fileList.fileName ?? "";
            fileUpload.base64filedata = fileList.fileBase64 ?? "";
          }
        }

        //延長申請理由※具体的に記入
        resArr.find(
          (item) => item.keyword === "extensionApplyReason"
        )!.setDefaultValue = res.extensionApplyReason;
        resArr.find(
          (item) => item.keyword === "extensionApplyReason"
        )!.disabled = !activeContents;
        //備考
        resArr.find((item) => item.keyword === "remark")!.setDefaultValue =
          res.remark;
        resArr.find((item) => item.keyword === "remark")!.disabled =
          !activeContents;
        setStatecorpInfoList(resArr);
        setActiveStepData(activeContents ? 0 : 1);
        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCheckAndSubmit = (data: any) => {
    setRequestData({
      applyId,
      contractorNo,
      premiumApplyId: resData.premiumApplyId,
      year: resData.year,
      premiumAmt: resData.premiumAmt,
      expectDeadline: data.applyDate,
      paymentDeadline: resData?.paymentDeadline,
      extensionApplyReason: data.extensionApplyReason,
      remark: data.remark,
      formList: handleFileUpload(data.fileUpload),
      screenId: "SCRC080009",
    });

    //希望する納付期限の日付確認
    if (dayjs(data.applyDate).isBefore(dayjs(resData.paymentDeadline))) {
      return setState({
        ...state,
        errorMsgId: "TKMSG_SCR0040E",
      });
    }

    //入出力項目.延長申請の理由証明書がない場合
    if (!data.fileUpload || data.fileUpload.length === 0) {
      setOpenUploadFile(true);
      return;
    }

    setIsSubmitOpen(true);
  };

  const handleOK = async (date?: any) => {
    // 掛金納付期限延長申請更新APIを呼び出す
    try {
      const res = await httpAPI_PRE004({
        ...requestData,
        receptionDay: date,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });

        navigate("/SCRC070008");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
        setIsSubmitOpen(false);
      }
    } catch (error) {
      navigate("/error");
    }
  };

  return (
    <Box className="SCRC080009" data-testid="SCRC080009PageForTest">
      <TkStrpper steps={stepperData} activeStep={activeStepData}></TkStrpper>

      <TkInfoManual rule={"\\n"}>{labelids.labelid.LBLC080009_01}</TkInfoManual>

      {isKiko && (
        <TkContractorAndNumberLine
          name="契約者（法人）名"
          no={`${businessState.common?.contractorNo!}:${businessState.common
            ?.contractorName!}`}
        />
      )}

      <TkFakeTable
        list={statecorpInfoList}
        ruleSchema={schema}
        onCheckAndSubmit={onCheckAndSubmit}
        buttonGroup={<TkBackButton />}
        submitButtonText={buttonName}
        showButton={showButton}
      />

      {isKiko ? (
        <TkDateDialog
          open={isSubmitOpen}
          onOky={handleOK}
          onCancel={() => setIsSubmitOpen(false)}
          minDate={new Date("1900/01/01")}
          maxDate={new Date()}
          requeried={true}
          title="確認（申請）"
          contextText="　申請を行いますが、問題ないでしょうか。"
          cancelText="いいえ"
          okText="はい"
        />
      ) : (
        <TkMessageDialog
          data-testid="dialogTest"
          open={isSubmitOpen}
          onOky={handleOK}
          onCancel={() => setIsSubmitOpen(false)}
          title="確認（機構へ提出する）"
          contextText="　機構へ提出を行いますが、問題ないでしょうか。"
          cancelText="いいえ"
          okText="はい"
        />
      )}
      <TkMessageDialog
        data-testid="dialogTest2"
        open={openUploadFile}
        onCancel={() => {
          setOpenUploadFile(false);
          setState({
            ...state,
            errorMsgId: "TKMSG_SCR0031E",
          });
        }}
        onOky={() => {
          setOpenUploadFile(false);
          setIsSubmitOpen(true);
        }}
        title="確認（延長申請の理由証明書）"
        contextText="　災害救助法適用による申請となりますか？"
        cancelText="いいえ"
        okText="はい"
      />
    </Box>
  );
};

export default SCRC080009;
